import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Dither from './Dither';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const Footer = () => {
    const { language } = useLanguage();
    const t = translations[language];
    const [hoveredLink, setHoveredLink] = useState(null);
    const currentYear = new Date().getFullYear();

    const directoryLinks = [
        { label: t.footer_home, to: '/' },
        { label: t.footer_labs, to: '/labs' },
        { label: t.footer_leadership, to: '/leadership' },
        { label: t.footer_about, to: '/leadership/abraham' },
        { label: 'ISHO', to: '/isho' },
    ];

    const engagementLinks = [
        { label: 'LinkedIn', href: 'https://linkedin.com/in/abrahammoratadeo' },
        { label: 'GitHub', href: 'https://github.com/AbrahamM007' },
        { label: 'Email', href: 'mailto:abraham@moratadeo.com' },
    ];

    const protocolLinks = [
        { label: t.footer_privacy, to: '/legal' },
        { label: t.footer_terms, to: '/legal' },
    ];

    return (
        <footer className="relative bg-[#09090b] text-white overflow-hidden">
            
            {/* Dither Background — same waveColor as Hero for cohesion */}
            <div className="absolute inset-0 z-0 opacity-[0.12] pointer-events-none mix-blend-screen">
                <Dither
                    waveColor={[56/255, 189/255, 248/255]}
                    colorNum={4}
                    waveAmplitude={0.2}
                    waveFrequency={2}
                    waveSpeed={0.015}
                />
            </div>

            {/* Ambient Glow */}
            <motion.div
                animate={{ scale: [1, 1.05, 1], opacity: [0.06, 0.12, 0.06] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60vw] h-[40vw] rounded-full blur-[150px] pointer-events-none z-0"
                style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.15), transparent 70%)' }}
            />

            {/* ── Top Divider ── */}
            <div className="relative z-10 mx-8 md:mx-16 lg:mx-24 border-t border-white/[0.06]" />

            {/* ── Main Footer Grid ── */}
            <div className="relative z-10 px-8 md:px-16 lg:px-24 pt-20 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12">

                    {/* Brand Column */}
                    <div className="lg:col-span-3 flex flex-col gap-10">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full bg-[#38bdf8] shadow-[0_0_8px_rgba(56,189,248,0.5)] animate-pulse" />
                                <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#38bdf8]">{t.footer_systems_online}</span>
                            </div>
                            <h2 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tighter leading-[0.9]">
                                MORA<br/>TADEO
                            </h2>
                            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/25">{t.footer_studio_location}</span>
                        </div>

                        <p className="font-mono text-[11px] leading-relaxed text-white/30 max-w-[260px]">
                            {t.footer_description}
                        </p>

                        {/* Status Indicators */}
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <div className="w-1 h-1 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.5)]" />
                                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/20">{t.footer_status_optimal}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-1 h-1 rounded-full bg-white/20" />
                                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/20">LOC: 34.0522° N, 118.2437° W</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-1 h-1 rounded-full bg-white/20" />
                                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/20">BUILD: AMT_V4.0</span>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Columns */}
                    <div className="lg:col-span-9">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-12 md:gap-16">

                            {/* Directory */}
                            <div className="flex flex-col gap-5">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-[1px] bg-[#38bdf8]/40" />
                                    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#38bdf8]/60">{t.footer_dir}</span>
                                </div>
                                <nav className="flex flex-col gap-3">
                                    {directoryLinks.map((link, i) => (
                                        <Link
                                            key={link.label}
                                            to={link.to}
                                            onMouseEnter={() => setHoveredLink(`dir-${i}`)}
                                            onMouseLeave={() => setHoveredLink(null)}
                                            className="group flex items-center gap-3 transition-all duration-300"
                                        >
                                            <div className={`w-1 h-1 rounded-full transition-all duration-300 ${hoveredLink === `dir-${i}` ? 'bg-[#38bdf8] shadow-[0_0_6px_rgba(56,189,248,0.4)] scale-150' : 'bg-white/10'}`} />
                                            <span className={`font-mono text-xs uppercase tracking-[0.15em] transition-all duration-300 ${hoveredLink === `dir-${i}` ? 'text-white translate-x-1' : 'text-white/40'}`}>
                                                {link.label}
                                            </span>
                                        </Link>
                                    ))}
                                </nav>
                            </div>

                            {/* Engagement */}
                            <div className="flex flex-col gap-5">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-[1px] bg-[#38bdf8]/40" />
                                    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#38bdf8]/60">{t.footer_eng}</span>
                                </div>
                                <nav className="flex flex-col gap-3">
                                    {engagementLinks.map((link, i) => (
                                        <a
                                            key={link.label}
                                            href={link.href}
                                            target={link.href.startsWith('mailto') ? undefined : '_blank'}
                                            rel="noreferrer"
                                            onMouseEnter={() => setHoveredLink(`eng-${i}`)}
                                            onMouseLeave={() => setHoveredLink(null)}
                                            className="group flex items-center gap-3 transition-all duration-300"
                                        >
                                            <div className={`w-1 h-1 rounded-full transition-all duration-300 ${hoveredLink === `eng-${i}` ? 'bg-[#38bdf8] shadow-[0_0_6px_rgba(56,189,248,0.4)] scale-150' : 'bg-white/10'}`} />
                                            <span className={`font-mono text-xs uppercase tracking-[0.15em] transition-all duration-300 ${hoveredLink === `eng-${i}` ? 'text-white translate-x-1' : 'text-white/40'}`}>
                                                {link.label}
                                            </span>
                                            <svg className={`w-3 h-3 transition-all duration-300 ${hoveredLink === `eng-${i}` ? 'text-[#38bdf8] opacity-100 translate-x-0' : 'text-white/10 opacity-0 -translate-x-1'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M17 7H7M17 7V17" />
                                            </svg>
                                        </a>
                                    ))}
                                </nav>
                            </div>

                            {/* Protocols */}
                            <div className="flex flex-col gap-5">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-[1px] bg-[#38bdf8]/40" />
                                    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#38bdf8]/60">{t.footer_pro}</span>
                                </div>
                                <nav className="flex flex-col gap-3">
                                    {protocolLinks.map((link, i) => (
                                        <Link
                                            key={link.label}
                                            to={link.to}
                                            onMouseEnter={() => setHoveredLink(`pro-${i}`)}
                                            onMouseLeave={() => setHoveredLink(null)}
                                            className="group flex items-center gap-3 transition-all duration-300"
                                        >
                                            <div className={`w-1 h-1 rounded-full transition-all duration-300 ${hoveredLink === `pro-${i}` ? 'bg-[#38bdf8] shadow-[0_0_6px_rgba(56,189,248,0.4)] scale-150' : 'bg-white/10'}`} />
                                            <span className={`font-mono text-xs uppercase tracking-[0.15em] transition-all duration-300 ${hoveredLink === `pro-${i}` ? 'text-white translate-x-1' : 'text-white/40'}`}>
                                                {link.label}
                                            </span>
                                        </Link>
                                    ))}
                                </nav>


                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Bottom Bar ── */}
            <div className="relative z-10 mx-8 md:mx-16 lg:mx-24 border-t border-white/[0.04]" />
            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-8 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-6 font-mono text-[8px] text-white/25 uppercase tracking-[0.3em]">
                    <span>© {currentYear} MORATADEO</span>
                    <span className="w-[1px] h-3 bg-white/10 hidden md:block" />
                    <span className="hidden md:inline">{t.footer_sys_arch}</span>
                    <span className="w-[1px] h-3 bg-white/10 hidden md:block" />
                    <span className="hidden md:inline">LOS ANGELES</span>
                </div>
                
            </div>

        </footer>
    );
};

export default Footer;
